import type { UsageKey } from './api';

/** A range of local calendar days, both ends inclusive, as YYYY-MM-DD. */
export interface DayRange {
  from: string;
  to: string;
}

export type Preset = 'today' | '7d' | '28d';
export type Metric = 'credits' | 'requests';
export type Granularity = 'hour' | 'day';

/** Arcade serves at most 31 UTC days of hourly buckets; a local day can straddle two. */
export const MAX_RANGE_DAYS = 28;
/** Up to this many days draw one bar per hour; longer ranges draw one per day. */
export const HOURLY_MAX_DAYS = 7;
/** Categorical slots; a ninth key onwards folds into "Other". */
export const SERIES_SLOTS = 8;

const HOUR_MS = 3_600_000;
const pad = (value: number) => String(value).padStart(2, '0');

export function localDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function startOfDay(day: string): Date {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year, month - 1, date);
}

export function addDays(day: string, days: number): string {
  const date = startOfDay(day);
  date.setDate(date.getDate() + days);
  return localDate(date);
}

export function dayCount({ from, to }: DayRange): number {
  return Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / 86_400_000) + 1;
}

export function presetRange(preset: Preset, now = new Date()): DayRange {
  const today = localDate(now);
  const days = preset === 'today' ? 1 : preset === '7d' ? 7 : 28;
  return { from: addDays(today, 1 - days), to: today };
}

/** The UTC dates whose hourly buckets cover every local hour of `range`. */
export function requestWindow(range: DayRange): DayRange {
  const utcDate = (date: Date) => date.toISOString().slice(0, 10);
  const end = new Date(startOfDay(addDays(range.to, 1)).getTime() - 1);
  return { from: utcDate(startOfDay(range.from)), to: utcDate(end) };
}

/** Starts `range` no earlier than the day the first key existed; null if no key did. */
export function clipToKeys(range: DayRange, keys: Pick<UsageKey, 'createdAt'>[]): DayRange | null {
  const created = keys
    .map((key) => key.createdAt)
    .filter((value): value is string => Boolean(value))
    .map((value) => localDate(new Date(value)))
    .sort();
  if (created.length === 0) {
    return null;
  }
  const from = created[0] > range.from ? created[0] : range.from;
  return from > range.to ? null : { from, to: range.to };
}

export interface ChartSeries {
  id: string;
  label: string;
  /** 0-based categorical slot, or null for the folded "Other" series. */
  slot: number | null;
  revoked: boolean;
  keyIds: string[];
  credits: number[];
  requests: number[];
  totalCredits: number;
  totalRequests: number;
}

export interface UsageChartData {
  granularity: Granularity;
  /** Start of each bar; the last bar ends at `end`. */
  columns: Date[];
  end: Date;
  series: ChartSeries[];
}

function columnsFor(range: DayRange, granularity: Granularity): { columns: Date[]; end: Date } {
  const end = startOfDay(addDays(range.to, 1));
  const columns: Date[] = [];
  if (granularity === 'hour') {
    for (let t = startOfDay(range.from).getTime(); t < end.getTime(); t += HOUR_MS) {
      columns.push(new Date(t));
    }
  } else {
    for (let day = range.from; day <= range.to; day = addDays(day, 1)) {
      columns.push(startOfDay(day));
    }
  }
  return { columns, end };
}

function columnIndex(columns: Date[], end: Date, at: number): number {
  if (at < columns[0].getTime() || at >= end.getTime()) {
    return -1;
  }
  let low = 0;
  let high = columns.length - 1;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (columns[mid].getTime() <= at) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }
  return low;
}

export function keyLabel(key: Pick<UsageKey, 'name' | 'secretPrefix'>): string {
  return key.name || (key.secretPrefix ? `${key.secretPrefix}…` : 'Unnamed key');
}

/**
 * Bins each key's hourly buckets into the range's bars, one series per key.
 * Slots follow creation order over every key, revoked or not, so colours never
 * move with the range or with `includeRevoked`.
 */
export function buildChart(
  keys: UsageKey[],
  range: DayRange,
  { includeRevoked = true }: { includeRevoked?: boolean } = {},
): UsageChartData {
  const granularity: Granularity = dayCount(range) <= HOURLY_MAX_DAYS ? 'hour' : 'day';
  const { columns, end } = columnsFor(range, granularity);
  const ordered = [...keys].sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''));
  const colored = ordered.length > SERIES_SLOTS ? SERIES_SLOTS - 1 : ordered.length;
  const inRange = (key: UsageKey) => !key.createdAt || new Date(key.createdAt) < end;
  const shown = (key: UsageKey) => includeRevoked || !key.revokedAt;

  const empty = (): Omit<ChartSeries, 'id' | 'label' | 'slot' | 'revoked' | 'keyIds'> => ({
    credits: columns.map(() => 0),
    requests: columns.map(() => 0),
    totalCredits: 0,
    totalRequests: 0,
  });
  const series: ChartSeries[] = ordered.slice(0, colored).map((key, slot) => ({
    id: key.id,
    label: keyLabel(key),
    slot,
    revoked: Boolean(key.revokedAt),
    keyIds: [key.id],
    ...empty(),
  }));
  const other: ChartSeries = {
    id: 'other',
    label: 'Other keys',
    slot: null,
    revoked: false,
    keyIds: ordered
      .slice(colored)
      .filter(shown)
      .map((key) => key.id),
    ...empty(),
  };

  ordered.forEach((key, index) => {
    if (!shown(key)) {
      return;
    }
    const target = index < colored ? series[index] : other;
    for (const bucket of key.usage) {
      const column = columnIndex(columns, end, new Date(bucket.start).getTime());
      if (column < 0) {
        continue;
      }
      target.credits[column] += bucket.credits;
      target.requests[column] += bucket.requests;
      target.totalCredits += bucket.credits;
      target.totalRequests += bucket.requests;
    }
  });

  const visible = series.filter(
    (entry, index) => shown(ordered[index]) && (inRange(ordered[index]) || entry.totalRequests),
  );
  if (other.keyIds.length > 0) {
    visible.push(other);
  }
  return { granularity, columns, end, series: visible };
}
